/**
 * Kiosk calls, as built in `workers/api/src/kiosk/{admin,device}-routes.ts`.
 * Pairing and revoking use the STAFF client; everything under `/kiosk/*` uses the kiosk client (`createClient` with `kiosk`),
 * which refuses any other path.
 */
import type { BranchHours, CheckinEvent, EntrySettings, KeySet, Plan } from "@helmr/checkin";
import type { ApiClient } from "./client";

export type KioskMode = "scanner" | "wall";
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
/** `branches.hours`: weekday → intervals. Missing or empty = closed that day; `null` overall = hours are not enforced. */
export type WeeklyHours = Partial<Record<Weekday, BranchHours[]>>;

export interface PairBody { branchId: string; label: string; mode: KioskMode }
export interface PairedKiosk {
  id: string; branchId: string; label: string; mode: KioskMode; status: "active" | "revoked";
}
/** The token (and, for a wall kiosk, its first secret) is shown once. */
export interface PairResult { kiosk: PairedKiosk; token: string; wall?: { kid: string; kioskSecret: string; notAfter: number } }

export const pairKiosk = (staff: ApiClient, body: PairBody, idempotencyKey?: string) =>
  staff.post<PairResult>("/kiosks", body, { idempotencyKey });
/** Staff bearer with `devices:delete`. A scanner kiosk also makes the server rotate the business key. */
export const deleteKiosk = (staff: ApiClient, id: string) => staff.del<{ kiosk: PairedKiosk; rotatedTo: string | null }>(`/kiosks/${id}`);

export interface KioskConfig {
  kiosk: { id: string; label: string; mode: KioskMode };
  branch: { id: string; name: string; timeZone: string; hours: WeeklyHours | null };
  entry: EntrySettings;
  window: { windowSeconds: number; skewWindows: number; wallMaxAgeWindows: number };
  offline: { maxOfflineAcceptHours: number; maxLateHours: number; maxBatchEvents: number };
  serverTime: number;
  /** The gym's member portal, `https://<gym host>/app` (D58): the wall QR links to `<memberBaseUrl>/k/<code>`. Sent whenever the branch's brand resolves. */
  memberBaseUrl?: string;
}
export interface KeysetResponse extends KeySet { serverTime: number }
export interface WallSecret { kid: string; kioskSecret: string; branchId: string; kioskId: string; notAfter: number; serverTime: number }

/** A roster row. Inactive members come back as `{ id, active: false }` so the kiosk can drop them. No phone, no email. */
export type RosterMember =
  | { id: string; active: false }
  | { id: string; active: true; name: string; photoUrl: string | null; plans: Plan[]; balanceDue: boolean };
export interface RosterPage { members: RosterMember[]; nextCursor: string | null; asOfDate: string; asOf: number }

export type ScanReason = string;
export interface ScanPerson { id: string; name: string; photoUrl: string | null }
export interface ScanResponse {
  decision: "allow" | "deny";
  colour: "green" | "amber" | "red";
  reasons: ScanReason[];
  subject: "member" | "staff";
  person: ScanPerson;
  planId?: string | null;
  punched?: boolean;
  checkinId: string;
}

export interface SyncOutcome { eventId: string; status: "accepted" | "rejected"; flags: string[]; memberId?: string }
export interface SyncResponse { batchId: string; outcomes: SyncOutcome[]; acknowledged: string[] }

/** `signal` lets the scanner give up after 2 s and decide offline. */
export const getConfig = (k: ApiClient, signal?: AbortSignal) => k.get<KioskConfig>("/kiosk/config", undefined, signal);
export const getKeyset = (k: ApiClient, signal?: AbortSignal) => k.get<KeysetResponse>("/kiosk/keyset", undefined, signal);
export const getWallSecret = (k: ApiClient, signal?: AbortSignal) => k.get<WallSecret>("/kiosk/wall-secret", undefined, signal);
export const getRoster = (k: ApiClient, q: { since?: number | null; cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
  k.get<RosterPage>("/kiosk/roster", { since: q.since ?? undefined, cursor: q.cursor ?? undefined, limit: q.limit }, signal);
export const postScan = (k: ApiClient, code: string, signal?: AbortSignal) =>
  k.request<ScanResponse>("POST", "/kiosk/scan", { body: { code }, signal });
/** Only `batchId` and `events` are sent: the server takes the kiosk id from the credential and ignores the body's. */
export const postSync = (k: ApiClient, batchId: string, events: CheckinEvent[], signal?: AbortSignal) =>
  k.request<SyncResponse>("POST", "/kiosk/sync", { body: { batchId, events }, signal, idempotencyKey: batchId });
